import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import './NewDashboard.css';
import './AdminDashboard.css';
import './AlumnoPanel.css';
import ColegioService from '../services/ColegioService';
import { useAuth } from '../contexts/AuthContext';
import cursoService from '../services/CursoService';
import ActiveColegioBadge from './common/ActiveColegioBadge';
import Alert from './common/Alert';
import UserSessionMenu from './common/UserSessionMenu';
import AlumnoMenu from './common/menus/AlumnoMenu';

const AlumnoPanel = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [alert, setAlert] = useState({ show: false, message: '', type: 'error', title: '' });
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [panel, setPanel] = useState({
    cursos: [],
    totalCursos: 0,
    totalAulas: 0
  });

  const roleName = (user?.roleName || user?.nombreRol || user?.role || '').toLowerCase();
  const isAlumno = roleName.includes('alumno');

  const activeColegio = useMemo(() => {
    return ColegioService.getActiveColegio() || ColegioService.getColegios()?.[0] || null;
  }, []);
  const activeColegioId = useMemo(() => {
    return (
      ColegioService.getActiveColegioId() ||
      activeColegio?.id ||
      activeColegio?.Id ||
      null
    );
  }, [activeColegio]);
  const activeColegioName = activeColegio?.nombre || activeColegio?.name || activeColegio?.descripcion || 'Colegio';

  const loadPanel = useCallback(async () => {
    if (!activeColegioId) {
      setError('No se pudo identificar el colegio activo del alumno.');
      setPanel({ cursos: [], totalCursos: 0, totalAulas: 0 });
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      const response = await cursoService.getAlumnoPanel({ idColegio: activeColegioId });
      const cursos = Array.isArray(response?.cursos) ? response.cursos : Array.isArray(response?.Cursos) ? response.Cursos : [];
      setPanel({
        cursos,
        totalCursos: response?.totalCursos ?? response?.TotalCursos ?? cursos.length,
        totalAulas: response?.totalAulas ?? response?.TotalAulas ?? cursos.reduce((acc, curso) => acc + (curso?.aulas?.length || curso?.Aulas?.length || 0), 0)
      });
    } catch (err) {
      setError(err.message || 'No pudimos cargar tus cursos y aulas.');
      setPanel({ cursos: [], totalCursos: 0, totalAulas: 0 });
    } finally {
      setIsLoading(false);
    }
  }, [activeColegioId]);

  useEffect(() => {
    loadPanel();
  }, [loadPanel]);

  const filteredCursos = useMemo(() => {
    const term = search.trim().toLowerCase();
    const source = Array.isArray(panel.cursos) ? panel.cursos : [];
    if (!term) {
      return source;
    }

    return source
      .map((curso) => {
        const nombreCurso = (curso?.nombre || curso?.Nombre || '').toLowerCase();
        const division = (curso?.division || curso?.Division || '').toLowerCase();
        const ciclo = String(curso?.cicloLectivo || curso?.CicloLectivo || '').toLowerCase();
        const anio = String(curso?.valorAnio || curso?.ValorAnio || '').toLowerCase();
        const aulas = Array.isArray(curso?.aulas) ? curso.aulas : Array.isArray(curso?.Aulas) ? curso.Aulas : [];

        const courseMatches =
          nombreCurso.includes(term) ||
          division.includes(term) ||
          ciclo.includes(term) ||
          anio.includes(term);

        if (courseMatches) {
          return curso;
        }

        const filteredAulas = aulas.filter((aula) => {
          const nombreAula = (aula?.nombre || aula?.Nombre || '').toLowerCase();
          const nombreMateria = (aula?.nombreMateria || aula?.NombreMateria || '').toLowerCase();
          const nickname = (aula?.nicknameUrl || aula?.NicknameUrl || '').toLowerCase();
          return (
            nombreAula.includes(term) ||
            nombreMateria.includes(term) ||
            nickname.includes(term)
          );
        });

        if (filteredAulas.length === 0) {
          return null;
        }

        return {
          ...curso,
          aulas: filteredAulas,
          Aulas: filteredAulas
        };
      })
      .filter(Boolean);
  }, [panel.cursos, search]);

  const totalAulasVisibles = useMemo(() => {
    return filteredCursos.reduce((acc, curso) => {
      const aulas = Array.isArray(curso?.aulas) ? curso.aulas : Array.isArray(curso?.Aulas) ? curso.Aulas : [];
      return acc + aulas.length;
    }, 0);
  }, [filteredCursos]);

  const totalAulasPrivadas = useMemo(() => {
    return filteredCursos.reduce((acc, curso) => {
      const aulas = Array.isArray(curso?.aulas) ? curso.aulas : Array.isArray(curso?.Aulas) ? curso.Aulas : [];
      return acc + aulas.filter((aula) => !(aula?.aulaPublica ?? aula?.AulaPublica ?? false)).length;
    }, 0);
  }, [filteredCursos]);

  const handleOpenAula = (targetAulaId, isPublica) => {
    if (!targetAulaId) {
      return;
    }

    const targetUrl = `/aula?id=${encodeURIComponent(targetAulaId)}&aulaPublica=${isPublica ? '1' : '0'}`;
    window.open(targetUrl, '_blank', 'noopener,noreferrer');
  };

  const handleMenuNavigate = (item) => {
    if (!item?.path) return;
    setIsSidebarOpen(false);
    navigate(item.path, item.state ? { state: item.state } : undefined);
  };

  if (!isAlumno) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className={`new-dashboard student-panel-page ${isSidebarOpen ? 'sidebar-open' : ''}`}>
      {isSidebarOpen && <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)} />}
      <aside className={`dashboard-sidebar ${isSidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="fas fa-graduation-cap"></i>
            <span>aluxIA</span>
          </div>
        </div>

        <AlumnoMenu activeId="panel" onNavigate={handleMenuNavigate} />

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className="fas fa-user-graduate"></i>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Alumno'}</span>
              <span className="user-role">Alumno</span>
            </div>
          </div>
        </div>
      </aside>

      <main className="dashboard-main">
        <header className="dashboard-header student-panel-header">
          <div className="header-left">
            <button
              type="button"
              className="sidebar-toggle"
              onClick={() => setIsSidebarOpen((prev) => !prev)}
              title="Mostrar menú"
            >
              <i className="fas fa-bars"></i>
            </button>
            <div>
              <h1 className="page-title">
                <i className="fas fa-user-graduate"></i>
                Mi espacio
              </h1>
              <p className="page-subtitle">
                Cursos y aulas disponibles en {activeColegioName}
              </p>
            </div>
          </div>

          <div className="header-right">
            <ActiveColegioBadge />
            <UserSessionMenu user={user} onLogout={logout} userFallbackLabel="Alumno" />
          </div>
        </header>

        <div className="dashboard-content student-panel-content">
        {alert.show && (
          <Alert
            title={alert.title}
            message={alert.message}
            type={alert.type}
            onClose={() => setAlert((prev) => ({ ...prev, show: false }))}
          />
        )}

        <section className="student-panel-hero">
          <div className="student-panel-welcome">
            <span className="student-panel-kicker">Alumno</span>
            <h2>Hola {user?.displayName || user?.name || 'estudiante'}</h2>
            <p>Desde acá podés ver todos tus cursos y entrar directamente a las aulas disponibles.</p>
          </div>

          <div className="student-panel-stats">
            <article className="student-stat-card">
              <span className="student-stat-label">Cursos</span>
              <strong>{panel.totalCursos}</strong>
            </article>
            <article className="student-stat-card">
              <span className="student-stat-label">Aulas</span>
              <strong>{panel.totalAulas}</strong>
            </article>
            <article className="student-stat-card">
              <span className="student-stat-label">Aulas privadas</span>
              <strong>{totalAulasPrivadas}</strong>
            </article>
          </div>
        </section>

        <section className="content-card student-panel-card">
          <div className="card-header student-panel-toolbar">
            <div className="card-title">
              <i className="fas fa-layer-group"></i>
              <span>Mis cursos y aulas</span>
            </div>

            <div className="professor-actions">
              <div className="professor-search">
                <i className="fas fa-search" aria-hidden="true" />
                <input
                  type="text"
                  placeholder="Buscar por curso, aula o materia"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <button className="refresh-btn" type="button" onClick={loadPanel} disabled={isLoading}>
                <i className={`fas ${isLoading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} />
              </button>
            </div>
          </div>

          <div className="card-content">
            {isLoading && (
              <div className="data-table-loading">
                <div className="loading-spinner">
                  <i className="fas fa-spinner fa-spin"></i>
                </div>
                <p>Cargando tu panel...</p>
              </div>
            )}

            {!isLoading && error && (
              <div className="data-table-empty">
                <div className="empty-icon">
                  <i className="fas fa-exclamation-triangle"></i>
                </div>
                <p>{error}</p>
                <button className="refresh-btn" type="button" onClick={loadPanel}>
                  <i className="fas fa-sync-alt" /> Reintentar
                </button>
              </div>
            )}

            {!isLoading && !error && filteredCursos.length === 0 && (
              <div className="data-table-empty">
                <div className="empty-icon">
                  <i className="fas fa-school"></i>
                </div>
                <p>
                  {search.trim()
                    ? 'No encontramos cursos o aulas que coincidan con tu búsqueda.'
                    : 'Todavía no tienes cursos o aulas asignadas en este colegio.'}
                </p>
              </div>
            )}

            {!isLoading && !error && filteredCursos.length > 0 && (
              <>
                <div className="student-panel-results">
                  Mostrando {filteredCursos.length} cursos y {totalAulasVisibles} aulas.
                </div>

                <div className="student-course-grid">
                  {filteredCursos.map((curso) => {
                    const cursoId = curso?.id || curso?.Id;
                    const nombre = curso?.nombre || curso?.Nombre || 'Curso';
                    const aulas = Array.isArray(curso?.aulas) ? curso.aulas : Array.isArray(curso?.Aulas) ? curso.Aulas : [];
                    const valorAnio = curso?.valorAnio || curso?.ValorAnio || '—';
                    const division = curso?.division || curso?.Division || '—';
                    const cicloLectivo = curso?.cicloLectivo || curso?.CicloLectivo || '—';

                    return (
                      <article key={cursoId || nombre} className="student-course-card">
                        <div className="student-course-header">
                          <div>
                            <h3>{nombre}</h3>
                            <p>{valorAnio}° · División {division} · Ciclo {cicloLectivo}</p>
                          </div>
                          <span className="student-course-badge">
                            {aulas.length} {aulas.length === 1 ? 'aula' : 'aulas'}
                          </span>
                        </div>

                        {aulas.length === 0 ? (
                          <div className="student-course-empty">
                            <i className="fas fa-door-open"></i>
                            <span>Este curso todavía no tiene aulas activas.</span>
                          </div>
                        ) : (
                          <div className="student-aula-list">
                            {aulas.map((aula) => {
                              const aulaId = aula?.id || aula?.Id;
                              const aulaNombre = aula?.nombre || aula?.Nombre || 'Aula';
                              const nombreMateria = aula?.nombreMateria || aula?.NombreMateria || 'Materia';
                              const isPublica = aula?.aulaPublica ?? aula?.AulaPublica ?? false;

                              return (
                                <div key={aulaId || aulaNombre} className="student-aula-item">
                                  <div className="student-aula-info">
                                    <div className="student-aula-title-row">
                                      <strong>{aulaNombre}</strong>
                                      <span className={`student-aula-pill ${isPublica ? 'public' : 'private'}`}>
                                        {isPublica ? 'Pública' : 'Privada'}
                                      </span>
                                    </div>
                                    <span className="student-aula-subtitle">{nombreMateria}</span>
                                    {(aula?.descripcion || aula?.Descripcion) && (
                                      <p>{aula?.descripcion || aula?.Descripcion}</p>
                                    )}
                                  </div>

                                  <button
                                    type="button"
                                    className="student-aula-enter-btn"
                                    onClick={() => handleOpenAula(aulaId, isPublica)}
                                  >
                                    Ingresar
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </article>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </section>
        </div>
      </main>
    </div>
  );
};

export default AlumnoPanel;
